import { useEffect, useRef, useState, type MouseEvent } from 'react'
import {
  api,
  type FeedbackItem,
  type FeedbackPanel as Totals,
  type TriageStatus,
} from '../lib/api.ts'
import { useAutoRefresh } from '../lib/useAutoRefresh.ts'
import { AccountFilter } from '../components/AccountFilter.tsx'
import { Button } from '../components/Button.tsx'
import { FeedbackCard } from '../components/FeedbackCard.tsx'
import { DateRange, type DateWindow } from '../components/DateRange.tsx'
import { Tooltip } from '../components/Tooltip.tsx'
import { Pagination } from '../components/Pagination.tsx'
import { icons } from '../components/icons.ts'
import { TRIAGE_FILTER_LABEL, label } from '../lib/vocabulary.ts'
import type { FeedbackReviewFilters } from '../lib/feedbackReviewSession.ts'

/**
 * Acompanhamento de feedbacks (#405).
 *
 * The conferentes' feedback as it arrives, newest first, for somebody on our
 * side to classify: concordo (the feedback is pertinent) or discordo. The
 * false positives and negatives that follow from it are counted in the filter
 * row, over the same period and pharmacy as the list -- one filter for both,
 * rather than a second screen with its own.
 *
 * "As it arrives" is a poll, like the queue's: feedback lands when a
 * conferência is concluded, and a beat of fifteen seconds is well inside how
 * fast anybody reads it.
 */
const STATUSES: ReadonlyArray<{ key: TriageStatus; label: string }> = [
  { key: 'pending', label: 'Não classificados' },
  { key: 'agree', label: label(TRIAGE_FILTER_LABEL, 'agree') },
  { key: 'disagree', label: label(TRIAGE_FILTER_LABEL, 'disagree') },
  { key: 'all', label: 'Todos' },
]

/**
 * Where the list was, kept on its own history entry.
 *
 * Opening a feedback goes to the conferência, and Back used to land on the
 * first page of "não classificados" at the top -- somebody working down a long
 * list lost their place on every case they checked. The filter, the page and
 * the card they opened ride on this entry's `history.state`, so Back (and a
 * reload) comes back to the same page with that card in view.
 */
interface QueuePlace {
  readonly status: TriageStatus
  readonly account: string
  readonly range?: DateWindow | undefined
  readonly page: number
  readonly opened?: string | undefined
}

function savedPlace(): QueuePlace | undefined {
  const saved = (window.history.state as { feedbackQueue?: QueuePlace } | null)?.feedbackQueue
  return saved !== undefined && typeof saved.page === 'number' ? saved : undefined
}

function keepPlace(place: QueuePlace): void {
  const state = (window.history.state as Record<string, unknown> | null) ?? {}
  window.history.replaceState({ ...state, feedbackQueue: place }, '')
}

export function FeedbackQueue({
  onBack,
  onOpenVerification,
  initialFilters,
  initialPage,
}: {
  onBack: () => void
  onOpenVerification: (
    item: FeedbackItem,
    filters: FeedbackReviewFilters,
    page: number,
    event: MouseEvent<HTMLAnchorElement>,
  ) => void
  initialFilters?: FeedbackReviewFilters | undefined
  initialPage?: number | undefined
}) {
  const [saved] = useState(savedPlace)
  const [status, setStatus] = useState<TriageStatus>(
    initialFilters?.status ?? saved?.status ?? 'pending',
  )
  const [account, setAccount] = useState(
    initialFilters?.account ?? (initialFilters === undefined ? saved?.account : undefined) ?? '',
  )
  // Unbounded by default: an old feedback nobody classified must not hide.
  const [range, setRange] = useState<DateWindow>(
    initialFilters === undefined
      ? (saved?.range ?? { from: '', to: '' })
      : { from: initialFilters.from ?? '', to: initialFilters.to ?? '' },
  )
  const [page, setPage] = useState(initialPage ?? saved?.page ?? 1)
  const [totals, setTotals] = useState<Totals | undefined>()
  /** The card opened before leaving, to bring back into view once loaded. */
  const returningTo = useRef(saved?.opened)
  const [highlighted, setHighlighted] = useState<string | undefined>()

  useEffect(() => {
    keepPlace({ status, account, range, page })
  }, [status, account, range, page])

  /*
   * The scroll is ours while this screen is up.
   *
   * On Back the browser restores the position it saved for this entry, and
   * the entry is rewritten as a card is opened -- so what it restored was the
   * top of the page, after the card had already been brought into view.
   */
  useEffect(() => {
    const before = window.history.scrollRestoration
    window.history.scrollRestoration = 'manual'
    return () => {
      window.history.scrollRestoration = before
    }
  }, [])

  const [items, setItems] = useState<FeedbackItem[]>([])
  const [total, setTotal] = useState(0)
  const [pageSize, setPageSize] = useState(50)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | undefined>()
  const refresh = useAutoRefresh(true, 15_000)

  useEffect(() => {
    const id = returningTo.current
    if (id === undefined || items.length === 0) return
    returningTo.current = undefined
    // It may have left this filter since -- classified from inside, say.
    if (!items.some((item) => item.id === id)) return
    // Twice: once on the first paint, and once after the browser has had its
    // own go at the position (restoration is off while this screen is up, but
    // a late layout -- a note expanding -- can still move the card).
    const bring = () =>
      document
        .querySelector(`[data-feedback-id="${CSS.escape(id)}"]`)
        ?.scrollIntoView({ block: 'center' })
    requestAnimationFrame(bring)
    const again = setTimeout(bring, 300)

    setHighlighted(id)
    const clear = setTimeout(() => setHighlighted(undefined), 2500)
    return () => {
      clearTimeout(again)
      clearTimeout(clear)
    }
  }, [items])

  useEffect(() => {
    // A beat is quiet: blanking the list every fifteen seconds for news that
    // usually amounts to nothing is what the queue stopped doing.
    if (!refresh.takeBackground()) setLoading(true)
    let current = true
    api
      .feedbackItems({ status, page, ...range, ...(account === '' ? {} : { account }) })
      .then((r) => {
        if (!current) return
        setItems(r.items)
        setTotal(r.total)
        setPageSize(r.pageSize)
        // Judgements can remove the last item on the page we are returning to.
        setPage((currentPage) =>
          Math.min(currentPage, Math.max(1, Math.ceil(r.total / r.pageSize))),
        )
        setError(undefined)
      })
      .catch((cause: unknown) => {
        if (current) setError(cause instanceof Error ? cause.message : 'falha ao carregar')
      })
      .finally(() => {
        if (current) setLoading(false)
      })
    return () => {
      current = false
    }
    // `refresh` is a stable object; its token is what moves.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, account, range, page, refresh.token])

  useEffect(() => {
    let current = true
    api
      .feedbackPanel({ ...range, ...(account === '' ? {} : { account }) })
      .then((r) => {
        if (current) setTotals(r)
      })
      .catch(() => {
        // The counts are a summary; the list says what failed.
      })
    return () => {
      current = false
    }
    // Recounted on the list's beat too, so a concordo shows in the totals.
  }, [range, account, refresh.token])

  const reload = () => {
    refresh.markBackground()
    refresh.refresh()
  }
  const filters: FeedbackReviewFilters = {
    status,
    account: account || undefined,
    from: range.from || undefined,
    to: range.to || undefined,
  }

  return (
    <div className="flex flex-col gap-5 p-5 pt-0">
      <div className="flex flex-wrap items-center gap-3">
        <Button
          variant="tertiary"
          tone="neutral"
          size="small"
          leadingIcon={icons.back}
          onClick={onBack}
        >
          Fila
        </Button>
        <h1 className="text-tH4 text-text-strong">Acompanhamento de feedbacks</h1>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex gap-1" role="radiogroup" aria-label="Situação">
          {STATUSES.map((option) => (
            <Button
              key={option.key}
              role="radio"
              aria-checked={status === option.key}
              variant={status === option.key ? 'primary' : 'secondary'}
              tone={status === option.key ? 'brand' : 'neutral'}
              size="small"
              onClick={() => {
                setStatus(option.key)
                setPage(1)
              }}
            >
              {option.label}
            </Button>
          ))}
        </div>
        <DateRange
          label="Recebido"
          value={range}
          onChange={(next) => {
            setRange(next)
            setPage(1)
          }}
        />
        <AccountFilter
          value={account}
          onChange={(next) => {
            setAccount(next)
            setPage(1)
          }}
        />
        {totals !== undefined && <TotalsLine totals={totals} />}
      </div>

      {error !== undefined && <p className="text-tP text-text-error">{error}</p>}

      {loading && items.length === 0 ? (
        <p className="text-tP text-text-weak">Carregando…</p>
      ) : items.length === 0 ? (
        <p className="text-tP text-text-weak">
          {status === 'pending' ? 'Nenhum feedback por classificar.' : 'Nenhum feedback aqui.'}
        </p>
      ) : (
        <ul className="flex flex-col gap-3">
          {items.map((item) => (
            <FeedbackCard
              key={item.id}
              item={item}
              highlighted={item.id === highlighted}
              reviewRoute={{ name: 'feedbackReview', eventId: item.id, filters, queuePage: page }}
              onOpen={(opened, event) => {
                keepPlace({ status, account, range, page, opened: opened.id })
                onOpenVerification(opened, filters, page, event)
              }}
              onSave={async (verdict, note) => {
                await api.triageFeedback(item.id, verdict, note)
                reload()
              }}
              onClear={async () => {
                await api.clearFeedbackTriage(item.id)
                reload()
              }}
            />
          ))}
        </ul>
      )}

      <Pagination
        currentPage={page}
        totalPages={Math.max(1, Math.ceil(total / pageSize))}
        onPageChange={setPage}
      />
    </div>
  )
}

/**
 * The false positives and negatives, in a line.
 *
 * What was a screen of its own: the same counts, over the same filters as the
 * list beside them, where somebody classifying can see them move.
 */
function TotalsLine({ totals }: { totals: Totals }) {
  const parts: Array<{ label: string; value: number; hint: string }> = [
    {
      label: 'falsos positivos',
      value: totals.total.falsePositives,
      hint: 'Apontamos e não havia divergência: o conferente tinha razão.',
    },
    {
      label: 'falsos negativos',
      value: totals.total.falseNegatives,
      hint: 'Dissemos que estava certo e não estava.',
    },
    {
      label: 'IA acertou',
      value: totals.total.aiRight,
      hint: 'O conferente contestou e não tinha razão.',
    },
  ]
  return (
    <div className="text-tSmall text-text-weak ml-auto flex flex-wrap items-center gap-x-3 gap-y-1 tabular-nums">
      {parts.map((part) => (
        <Tooltip key={part.label} text={part.hint}>
          <span>
            <span className="text-text-strong font-semibold">{part.value}</span> {part.label}
          </span>
        </Tooltip>
      ))}
      <span aria-hidden="true" className="text-stroke-weak">
        |
      </span>
      <span>
        <span className="text-text-strong font-semibold">{totals.total.classified}</span>{' '}
        classificados · {totals.pending} por classificar
      </span>
    </div>
  )
}
